import React from 'react';
import { Link } from 'react-router-dom';
import SEO, { SEOPages } from '../components/SEO';
import { 
  Shield, 
  TrendingUp, 
  AlertCircle, 
  CheckCircle, 
  ArrowRight,
  Briefcase,
  Calculator,
  FileText,
  Award,
  DollarSign,
  Clock,
  Target
} from 'lucide-react';
import LeadMagnetBox from '../components/LeadMagnetBox';
import MiniSimulateurPrevoyanceIndependants from '../components/MiniSimulateurPrevoyanceIndependants';

const GuidePrevoyanceIndependants = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B0F12] via-[#0C1D3D] to-[#0B0F12]">
      <SEO {...SEOPages.guidePrevoyanceIndependants} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-500/10" />
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-400 via-pink-400 to-rose-400 bg-clip-text text-transparent leading-tight">
              Guide Prévoyance Indépendants Suisse
            </h1>
            <p className="text-xl text-gray-300 mb-4">
              Sécurisez votre avenir sans 2ème pilier : stratégies, plafonds et optimisation
            </p>
            <p className="text-sm text-gray-400">
              Par ATOM Group SA • Mis à jour le {new Date().toLocaleDateString('fr-CH', { day: 'numeric', month: 'long', year: 'numeric' })} • Lecture : 15 minutes
            </p>
          </div>

          {/* Lead Magnet Top */}
          <LeadMagnetBox position="top" />
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Applicabilité nationale */}
          <div className="p-5 bg-purple-500/10 border-l-4 border-purple-500 rounded-r-lg mb-8">
            <p className="text-gray-200 text-sm">
              <strong>🇨🇭 Guide applicable dans toute la Suisse</strong> • Les plafonds 3a et règles LPP sont fédéraux. Les économies fiscales varient selon les cantons, mais les stratégies sont valables pour tous les indépendants de Suisse romande (Vaud, Genève, Neuchâtel, Fribourg, Valais). <strong>Mis à jour : {new Date().toLocaleDateString('fr-CH', { month: 'long', year: 'numeric' })}</strong>
            </p>
          </div>
          
          {/* Introduction */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <h2 className="text-3xl font-bold text-white mb-6">Indépendant : les défis de la prévoyance</h2>
            <p className="text-gray-300 mb-4 leading-relaxed">
              En tant qu'indépendant en Suisse, vous jouissez d'une grande liberté professionnelle, mais vous devez aussi assumer seul votre prévoyance vieillesse. Contrairement aux salariés, vous n'avez pas de 2ème pilier (LPP) obligatoire, ce qui crée une lacune importante dans votre protection sociale.
            </p>
            <p className="text-gray-300 mb-4 leading-relaxed">
              Cette situation présente à la fois des défis et des opportunités : vous devez compenser l'absence du 2ème pilier, mais vous bénéficiez aussi de plafonds fiscaux bien plus avantageux pour construire votre prévoyance.
            </p>
            
            {/* ATOM Independence */}
            <div className="mt-6 p-4 bg-purple-500/10 border-l-4 border-purple-500 rounded-r-lg">
              <p className="text-gray-200 text-sm italic">
                <strong>ATOM Group est un cabinet 100% indépendant,</strong> non lié à une banque ou assurance. Nos recommandations sont objectives et basées uniquement sur votre intérêt.
              </p>
            </div>
          </div>

          {/* Lead Magnet après intro */}

          {/* CTA après introduction */}
          

          {/* Lacunes de prévoyance */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <AlertCircle className="w-8 h-8 text-red-400" />
              <h2 className="text-3xl font-bold text-white">Vos lacunes de prévoyance</h2>
            </div>

            <p className="text-gray-300 mb-6 leading-relaxed">
              Sans 2ème pilier, votre prévoyance repose uniquement sur l'AVS (1er pilier). Voici ce que vous risquez :
            </p>

            <div className="space-y-4">
              <div className="p-6 bg-red-500/10 border-l-4 border-red-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">❌ Rente de retraite insuffisante</h4>
                <p className="text-gray-300 text-sm">
                  L'AVS peut verser jusqu'à CHF 2'520/mois (montant maximal 2026 pour une personne ayant cotisé 44 années sans lacune). Avec un train de vie actif, cela représente une baisse drastique de votre niveau de vie.
                </p>
              </div>

              <div className="p-6 bg-red-500/10 border-l-4 border-red-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">❌ Pas de couverture perte de gain</h4>
                <p className="text-gray-300 text-sm">
                  En cas de maladie ou d'accident, aucune assurance ne vous verse automatiquement un salaire de remplacement (contrairement aux salariés protégés par la LPP).
                </p>
              </div>

              <div className="p-6 bg-red-500/10 border-l-4 border-red-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">❌ Protection décès/invalidité limitée</h4>
                <p className="text-gray-300 text-sm">
                  Vos proches et votre famille ne bénéficient que des prestations AVS de base, souvent insuffisantes pour maintenir leur niveau de vie.
                </p>
              </div>

              <div className="p-6 bg-red-500/10 border-l-4 border-red-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">❌ Pas de capital départ à la retraite</h4>
                <p className="text-gray-300 text-sm">
                  Les salariés peuvent retirer leur 2ème pilier en capital (plusieurs centaines de milliers de francs). Vous n'avez pas cet avantage.
                </p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-yellow-500/10 border-l-4 border-yellow-500 rounded-r-lg">
              <p className="text-gray-200 text-sm">
                <strong>Constat :</strong> L'AVS ne couvre en moyenne que 30 à 40% de votre dernier revenu d'activité. Il vous manque donc 60 à 70% à combler par vos propres moyens !
              </p>
            </div>
          </div>

          {/* CTA */}
          

          {/* Plafonds 3a spéciaux */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <DollarSign className="w-8 h-8 text-green-400" />
              <h2 className="text-3xl font-bold text-white">3ème pilier : vos plafonds spéciaux 2026</h2>
            </div>

            <p className="text-gray-300 mb-6 leading-relaxed">
              La bonne nouvelle : en tant qu'indépendant sans LPP, vous bénéficiez d'un plafond 3a bien supérieur aux salariés.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="p-6 bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-xl border border-blue-500/30">
                <h3 className="text-xl font-bold text-white mb-2">Affilié au 2ème pilier</h3>
                <p className="text-4xl font-bold text-blue-400 mb-3">CHF 7'258</p>
                <p className="text-gray-300 text-sm">Plafond annuel maximum</p>
                <p className="text-gray-400 text-xs mt-2">Salariés ou indépendants avec LPP</p>
              </div>

              <div className="p-6 bg-gradient-to-br from-purple-500/20 to-purple-600/20 rounded-xl border border-purple-500/30">
                <h3 className="text-xl font-bold text-white mb-2">Non affilié au 2ème pilier</h3>
                <p className="text-4xl font-bold text-purple-400 mb-3">CHF 36'288</p>
                <p className="text-gray-300 text-sm">20% du revenu net (max.)</p>
                <p className="text-gray-400 text-xs mt-2">Indépendants ou salariés sans LPP</p>
              </div>
            </div>

            <div className="p-6 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl border border-green-500/30 mb-6">
              <h4 className="font-bold text-white mb-3 flex items-center gap-2">
                <Calculator className="w-5 h-5 text-green-400" />
                Calcul du plafond pour indépendants
              </h4>
              <p className="text-gray-300 text-sm mb-3">
                <strong>Formule :</strong> 20% de votre revenu net AVS (après déduction des charges), plafonné à CHF 36'288
              </p>
              <p className="text-gray-300 text-sm">
                <strong>Exemple :</strong> Revenu net AVS de CHF 100'000 → Plafond 3a = CHF 20'000<br/>
                Revenu net AVS de CHF 200'000 → Plafond 3a = CHF 36'288 (plafond maximum)
              </p>
            </div>

            <div className="p-4 bg-purple-500/10 border-l-4 border-purple-500 rounded-r-lg">
              <p className="text-gray-200 text-sm">
                <strong>Avantage fiscal majeur :</strong> Chaque franc versé en 3a est déductible de votre revenu imposable. Selon votre canton et revenu, cela représente jusqu'à CHF 10'000-15'000 d'économies d'impôts par an !
              </p>
            </div>

            <p className="text-xs text-gray-400 mt-4 text-center italic">
              Sources : OFAS • Barèmes fiscaux cantonaux 2025 • Estimations ATOM Group SA
            </p>
          </div>

          {/* CTA */}
          

          {/* Simulateur */}
          <MiniSimulateurPrevoyanceIndependants />

          {/* Assurances obligatoires et recommandées */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-8 h-8 text-blue-400" />
              <h2 className="text-3xl font-bold text-white">Assurances indispensables</h2>
            </div>

            <p className="text-gray-300 mb-6 leading-relaxed">
              En plus du 3ème pilier, certaines assurances sont essentielles pour protéger votre activité et votre famille.
            </p>

            <div className="space-y-4">
              <div className="p-6 bg-red-500/10 border-l-4 border-red-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">🚨 Obligatoire : Assurance accidents</h4>
                <p className="text-gray-300 text-sm mb-2">
                  Si vous n'avez pas de salariés, vous devez obligatoirement couvrir le risque d'accident via votre assurance maladie (LAMal).
                </p>
                <p className="text-gray-400 text-xs">
                  Coût : environ CHF 30-50/mois selon votre activité
                </p>
              </div>

              <div className="p-6 bg-orange-500/10 border-l-4 border-orange-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">⚠️ Vivement recommandée : Assurance perte de gain maladie</h4>
                <p className="text-gray-300 text-sm mb-2">
                  L'assurance perte de gain maladie n'est pas obligatoire légalement mais fortement recommandée pour tous les indépendants. En cas de maladie, cette assurance vous verse une indemnité journalière (80 à 100% de votre revenu). Sans elle, vous n'avez aucun revenu pendant votre arrêt maladie.
                </p>
                <p className="text-gray-400 text-xs">
                  Coût : 2 à 4% de votre revenu assuré (délai d'attente : 30, 60 ou 90 jours)
                </p>
              </div>

              <div className="p-6 bg-blue-500/10 border-l-4 border-blue-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">✅ Recommandée : Assurance décès/invalidité</h4>
                <p className="text-gray-300 text-sm mb-2">
                  Protège votre famille en cas de décès ou d'invalidité. Compense l'absence de rentes LPP pour vos proches.
                </p>
                <p className="text-gray-400 text-xs">
                  Coût : variable selon l'âge, le montant assuré et votre état de santé
                </p>
              </div>

              <div className="p-6 bg-green-500/10 border-l-4 border-green-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">💼 Optionnelle : Assurance protection juridique</h4>
                <p className="text-gray-300 text-sm mb-2">
                  Couvre vos frais d'avocat en cas de litige avec un client, un fournisseur ou l'administration.
                </p>
                <p className="text-gray-400 text-xs">
                  Coût : CHF 200-400/an selon la couverture
                </p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-500/10 border-l-4 border-blue-500 rounded-r-lg">
              <p className="text-gray-200 text-sm italic">
                <strong>ATOM Group est un cabinet 100% indépendant.</strong> Nos recommandations sont objectives et basées uniquement sur votre intérêt.
              </p>
            </div>

            <div className="mt-6 p-4 bg-green-500/10 border-l-4 border-green-500 rounded-r-lg">
              <p className="text-gray-200 text-sm">
                <strong>💡 Avantage fiscal :</strong> En tant qu'indépendant, la plupart des primes d'assurance (LAMal, perte de gain, LCA) peuvent être comptabilisées comme charges professionnelles déductibles.
              </p>
            </div>
          </div>

          {/* CTA WhatsApp */}
          <div className="glass p-6 rounded-xl border border-green-500/30 mb-8 bg-gradient-to-r from-green-500/10 to-emerald-500/10">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="text-3xl">📲</div>
                <div>
                  <p className="text-white font-semibold text-lg">Une question rapide ?</p>
                  <p className="text-gray-300 text-sm">Réponse en 5 minutes</p>
                </div>
              </div>
              <a
                href="https://wa.me/41793754293"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all transform hover:scale-105 flex items-center gap-2 whitespace-nowrap"
              >
                Écrire sur WhatsApp
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Stratégies d'optimisation */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Target className="w-8 h-8 text-purple-400" />
              <h2 className="text-3xl font-bold text-white">5 stratégies pour optimiser votre prévoyance</h2>
            </div>

            <div className="space-y-6">
              <div className="p-6 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/30">
                <h4 className="text-xl font-bold text-white mb-3">1️⃣ Maximisez votre 3a chaque année</h4>
                <p className="text-gray-300 mb-2">
                  Versez le montant maximum autorisé (20% de votre revenu, max CHF 36'288) pour bénéficier de la déduction fiscale maximale.
                </p>
                <p className="text-green-400 font-semibold">
                  💡 Économie fiscale : jusqu'à CHF 10'000-15'000/an selon canton
                </p>
              </div>

              <div className="p-6 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl border border-blue-500/30">
                <h4 className="text-xl font-bold text-white mb-3">2️⃣ Adhérez volontairement au 2ème pilier</h4>
                <p className="text-gray-300 mb-2">
                  Si votre caisse de pension le permet, vous pouvez cotiser volontairement au LPP. Cela vous donne accès aux prestations décès/invalidité et constitue un capital retraite complémentaire.
                </p>
                <p className="text-gray-300 text-sm mb-2">
                  <strong>Important :</strong> Un indépendant ne peut racheter dans le 2e pilier que s'il est déjà affilié volontairement à une caisse de pension. Si aucune affiliation LPP volontaire n'est en place, aucun rachat n'est possible.
                </p>
                <p className="text-gray-400 text-sm">
                  Note : les cotisations sont également déductibles fiscalement
                </p>
              </div>

              <div className="p-6 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl border border-green-500/30">
                <h4 className="text-xl font-bold text-white mb-3">3️⃣ Ouvrez plusieurs comptes 3a</h4>
                <p className="text-gray-300 mb-2">
                  Comme les salariés, vous pouvez ouvrir plusieurs comptes 3a (idéalement 3 à 5) pour échelonner les retraits et réduire la fiscalité au moment de la retraite.
                </p>
                <p className="text-green-400 font-semibold">
                  💡 Économie fiscale au retrait : plusieurs milliers de francs
                </p>
              </div>

              <div className="p-6 bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-xl border border-orange-500/30">
                <h4 className="text-xl font-bold text-white mb-3">4️⃣ Souscrivez une assurance perte de gain solide</h4>
                <p className="text-gray-300 mb-2">
                  Choisissez une couverture à 100% de votre revenu avec un délai d'attente court (30 jours). C'est votre filet de sécurité en cas de coup dur.
                </p>
                <p className="text-gray-400 text-sm">
                  Les primes sont déductibles fiscalement en tant que charges d'exploitation
                </p>
              </div>

              <div className="p-6 bg-gradient-to-br from-pink-500/10 to-rose-500/10 rounded-xl border border-pink-500/30">
                <h4 className="text-xl font-bold text-white mb-3">5️⃣ Planifiez tôt votre retraite</h4>
                <p className="text-gray-300 mb-2">
                  Simulez régulièrement votre capital retraite et ajustez vos cotisations. Plus vous commencez tôt, plus l'effet des intérêts composés joue en votre faveur.
                </p>
                <p className="text-green-400 font-semibold">
                  💡 Commencer à 30 ans vs 40 ans = jusqu'à CHF 150'000 de différence
                </p>
              </div>
            </div>
          </div>

          {/* Lead Magnet Middle */}

          {/* Cas pratiques */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-8 h-8 text-purple-400" />
              <h2 className="text-3xl font-bold text-white">Cas pratiques</h2>
            </div>

            <div className="space-y-6">
              <div className="p-6 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-xl border border-blue-500/30">
                <h4 className="text-xl font-bold text-white mb-3">📊 Cas 1 : Sarah, consultante IT, 35 ans</h4>
                <p className="text-gray-300 mb-2"><strong>Situation :</strong> Revenu net CHF 120'000/an, célibataire</p>
                <p className="text-gray-300 mb-4"><strong>Objectif :</strong> Sécuriser sa retraite et optimiser sa fiscalité</p>
                <p className="text-green-400 font-semibold mb-2">✅ Solution ATOM :</p>
                <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
                  <li>Versement 3a maximum : CHF 24'000/an (20% de CHF 120'000)</li>
                  <li>Économie fiscale immédiate : ~CHF 7'200/an</li>
                  <li>Assurance perte de gain : 100% du revenu, 30 jours d'attente</li>
                  <li>Capital retraite projeté (65 ans) : ~CHF 950'000</li>
                </ul>
              </div>

              <div className="p-6 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/30">
                <h4 className="text-xl font-bold text-white mb-3">💼 Cas 2 : Marc, architecte, 45 ans avec famille</h4>
                <p className="text-gray-300 mb-2"><strong>Situation :</strong> Revenu net CHF 180'000, marié, 2 enfants</p>
                <p className="text-gray-300 mb-4"><strong>Objectif :</strong> Combler ses lacunes de prévoyance tardivement</p>
                <p className="text-green-400 font-semibold mb-2">✅ Solution ATOM :</p>
                <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
                  <li>Versement 3a maximum : CHF 36'288/an (plafond atteint)</li>
                  <li>Adhésion volontaire LPP pour prestations décès/invalidité</li>
                  <li>Assurance risque décès : capital CHF 500'000</li>
                  <li>Économie fiscale : ~CHF 13'000/an</li>
                </ul>
              </div>

              <div className="p-6 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl border border-green-500/30">
                <h4 className="text-xl font-bold text-white mb-3">🚀 Cas 3 : Julie, graphiste freelance, 28 ans</h4>
                <p className="text-gray-300 mb-2"><strong>Situation :</strong> Début d'activité, revenu variable CHF 60'000-80'000</p>
                <p className="text-gray-300 mb-4"><strong>Objectif :</strong> Construire progressivement sa prévoyance</p>
                <p className="text-green-400 font-semibold mb-2">✅ Solution ATOM :</p>
                <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
                  <li>3a progressif : CHF 12'000 la 1ère année, puis augmentation</li>
                  <li>Assurance perte de gain avec délai 60 jours (prime réduite)</li>
                  <li>3 comptes 3a pour optimiser les retraits futurs</li>
                  <li>Bénéfice de l'effet des intérêts composés (37 ans jusqu'à la retraite)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Erreurs à éviter */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <AlertCircle className="w-8 h-8 text-red-400" />
              <h2 className="text-3xl font-bold text-white">5 Erreurs fréquentes à éviter</h2>
            </div>

            <div className="space-y-4">
              {[
                {
                  title: "Négliger le 3ème pilier",
                  desc: "Beaucoup d'indépendants sous-estiment l'importance du 3a. C'est pourtant votre principale protection pour la retraite."
                },
                {
                  title: "Pas d'assurance perte de gain",
                  desc: "En cas de maladie longue durée, vous n'avez aucun revenu. Cette assurance est vitale pour votre sécurité financière."
                },
                {
                  title: "Reporter la prévoyance à plus tard",
                  desc: "Plus vous commencez tard, plus il sera difficile et coûteux de rattraper le retard. L'effet des intérêts composés est puissant."
                },
                {
                  title: "Ne pas profiter des avantages fiscaux",
                  desc: "Chaque franc non versé en 3a = avantage fiscal perdu définitivement. Versez le maximum autorisé chaque année."
                },
                {
                  title: "Oublier la protection de la famille",
                  desc: "Sans LPP, vos proches ne reçoivent que l'AVS en cas de décès. Pensez à une assurance décès complémentaire."
                }
              ].map((error, index) => (
                <div key={index} className="p-6 bg-red-500/10 border-l-4 border-red-500 rounded-r-lg">
                  <h4 className="font-bold text-white mb-2">❌ {error.title}</h4>
                  <p className="text-gray-300 text-sm">{error.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <h2 className="text-3xl font-bold text-white mb-6">Questions fréquentes</h2>
            
            <div className="space-y-4">
              {[
                {
                  q: "Puis-je cotiser au 2ème pilier en tant qu'indépendant ?",
                  a: "Oui, vous pouvez adhérer volontairement au 2ème pilier si votre caisse de pension le permet. Cela vous donne accès aux mêmes prestations qu'un salarié (rente, capital, prestations décès/invalidité)."
                },
                {
                  q: "Comment calculer mon plafond 3a exact ?",
                  a: "Prenez 20% de votre revenu net AVS (revenu après déduction des charges), avec un maximum de CHF 36'288 en 2026. Votre comptable peut vous aider à calculer ce montant précisément."
                },
                {
                  q: "Que se passe-t-il si je redeviens salarié ?",
                  a: "Votre plafond 3a revient au plafond standard (CHF 7'258). Vos avoirs 3a constitués restent acquis et continuent de fructifier."
                },
                {
                  q: "L'assurance perte de gain est-elle déductible ?",
                  a: "Oui, les primes d'assurance perte de gain maladie sont déductibles comme charges d'exploitation dans votre comptabilité."
                },
                {
                  q: "Quand commencer ma prévoyance en tant qu'indépendant ?",
                  a: "Le plus tôt possible ! Idéalement dès le début de votre activité indépendante. L'effet des intérêts composés est votre meilleur allié sur le long terme."
                }
              ].map((faq, index) => (
                <div key={index} className="p-6 bg-white/5 rounded-xl border border-white/10 hover:border-purple-500/30 transition-all">
                  <h4 className="font-bold text-white mb-2 flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    {faq.q}
                  </h4>
                  <p className="text-gray-300 text-sm ml-7">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Lead Magnet Bottom */}

          {/* Conclusion */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <h2 className="text-3xl font-bold text-white mb-6">Conclusion</h2>
            <p className="text-gray-300 mb-4 leading-relaxed">
              En tant qu'indépendant, vous êtes maître de votre prévoyance. Cette liberté s'accompagne d'une responsabilité : celle de construire activement votre sécurité financière pour la retraite.
            </p>
            <p className="text-gray-300 mb-6 leading-relaxed">
              <strong className="text-white">Les opportunités sont réelles :</strong> plafonds 3a majorés, déductions fiscales importantes, flexibilité totale. Mais elles demandent une action proactive et une planification rigoureuse.
            </p>

            <div className="p-6 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl border border-purple-500/30">
              <p className="text-white font-semibold mb-4">
                🎯 ATOM Group vous accompagne pour :
              </p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  Calculer vos plafonds 3a exacts et optimiser vos versements
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  Choisir les meilleures assurances (perte de gain, décès/invalidité)
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  Simuler votre capital retraite et ajuster votre stratégie
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  Maximiser vos économies fiscales chaque année
                </li>
              </ul>
            </div>

            <div className="mt-6 text-center">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-purple-600 hover:to-pink-700 transition-all transform hover:scale-105 text-lg"
              >
                Réserver mon analyse gratuite maintenant
                <ArrowRight className="w-6 h-6" />
              </Link>
              <p className="text-gray-400 text-sm mt-3">
                🕐 Rendez-vous de 30 minutes • 100% gratuit • Sans engagement
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* Sticky Banner */}

      {/* Exit Intent Popup */}
    </div>
  );
};

export default GuidePrevoyanceIndependants;
